import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useCart } from "@/contexts/CartContext";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";
import { ShoppingCart, Settings, Eye } from "lucide-react";
import HTMLContent from "@/components/HTMLContent";

interface Service {
  id: string;
  name: string;
  description: string;
  price: number;
  image_url: string;
  type: 'product' | 'service';
  category_name?: string;
  billing_type?: 'monthly' | 'quarterly' | 'biannual' | 'annual' | 'one_time';
}

const Services = () => {
  const getBillingTypeLabel = (billingType?: string) => {
    switch (billingType) {
      case 'monthly': return 'Mensual';
      case 'quarterly': return 'Trimestral';
      case 'biannual': return 'Semestral';
      case 'annual': return 'Anual';
      case 'one_time': return 'Pago Único';
      default: return 'Pago Único';
    }
  };

  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState(true);
  const { addToCart } = useCart();

  useEffect(() => {
    fetchServices();
  }, []);

  const fetchServices = async () => {
    try {
      const { data, error } = await supabase
        .from('products_2025_11_10_14_11')
        .select(`
          id,
          name,
          description,
          price,
          image_url,
          type,
          billing_type,
          categories_2025_11_10_14_11(name)
        `)
        .eq('type', 'service')
        .eq('is_active', true);

      if (error) throw error;

      const formattedServices = data?.map(service => ({
        ...service,
        category_name: service.categories_2025_11_10_14_11?.name
      })) || [];
      
      console.log('Services data:', formattedServices);
      setServices(formattedServices);
    } catch (error: any) {
      toast({
        title: "Error",
        description: "No se pudieron cargar los servicios",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = (service: Service) => {
    addToCart({
      id: service.id,
      name: service.name,
      price: service.price,
      type: service.type,
      image_url: service.image_url
    });
    toast({
      title: "Servicio agregado",
      description: `${service.name} se agregó al carrito`,
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[...Array(6)].map((_, i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader>
                <div className="h-48 bg-muted rounded-lg"></div>
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </CardHeader>
              <CardContent>
                <div className="h-3 bg-muted rounded w-full mb-2"></div>
                <div className="h-3 bg-muted rounded w-2/3"></div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      {/* Header */}
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4 bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">
          Nuestros Servicios
        </h1>
        <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
          Ofrecemos servicios profesionales de streaming, producción audiovisual y desarrollo web 
          adaptados a tus necesidades específicas.
        </p>
      </div>

      {/* Services Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {services.map((service) => (
          <Card key={service.id} className="group hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
            <CardHeader>
              <div className="h-48 rounded-lg overflow-hidden mb-4 bg-muted">
                {service.image_url ? (
                  <img 
                    src={service.image_url} 
                    alt={service.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Settings className="h-16 w-16 text-muted-foreground" />
                  </div>
                )}
              </div>
              {service.category_name && (
                <Badge variant="secondary" className="w-fit mb-2">
                  {service.category_name}
                </Badge>
              )}
              <CardTitle className="text-lg">{service.name}</CardTitle>
              <HTMLContent 
                content={service.description} 
                className="text-sm text-muted-foreground line-clamp-3"
              />
            </CardHeader>
            <CardContent>
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-2xl font-bold text-primary">
                      ${service.price.toFixed(2)}
                    </span>
                    {service.billing_type && service.billing_type !== 'one_time' && (
                      <span className="text-sm text-muted-foreground ml-2">
                        / {getBillingTypeLabel(service.billing_type)}
                      </span>
                    )}
                  </div>
                  <Badge variant="outline" className="text-green-600 border-green-600">
                    Disponible
                  </Badge>
                </div>
                <div className="text-center">
                  <Badge variant="secondary" className="text-xs">
                    Facturación: {getBillingTypeLabel(service.billing_type || 'one_time')}
                  </Badge>
                </div>
              </div>
              <div className="flex gap-2">
                <Link to={`/servicio/${service.id}`} className="flex-1">
                  <Button variant="outline" className="w-full">
                    <Eye className="mr-2 h-4 w-4" />
                    Ver Detalles
                  </Button>
                </Link>
                <Button 
                  className="flex-1" 
                  onClick={() => handleAddToCart(service)}
                >
                  <ShoppingCart className="mr-2 h-4 w-4" />
                  Contratar
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {services.length === 0 && !loading && (
        <div className="text-center py-12">
          <Settings className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
          <h3 className="text-xl font-semibold mb-2">No hay servicios disponibles</h3>
          <p className="text-muted-foreground">Pronto agregaremos más servicios a nuestro catálogo.</p>
        </div>
      )}

      {/* Contact CTA */}
      <div className="mt-16 text-center bg-gradient-to-r from-primary/10 to-accent/10 rounded-lg p-8">
        <h2 className="text-2xl font-bold mb-4">¿Necesitas un servicio personalizado?</h2>
        <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
          Cada proyecto es único. Contáctanos para discutir tus necesidades específicas 
          y crear una solución a medida para tu negocio.
        </p>
        <Button size="lg" className="bg-gradient-to-r from-primary to-accent hover:opacity-90">
          Contactar para Cotización
        </Button>
      </div>
    </div>
  );
};

export default Services;